import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';

import { TrainingTab } from '../components/preferences/TrainingTab';
import { BodyTab } from '../components/preferences/BodyTab';
import { ConnectionsTab } from '../components/preferences/ConnectionsTab';

const TABS = [
  { key: 'training', icon: '🏃', label: 'preferences.tab_training' },
  { key: 'body', icon: '❤️', label: 'preferences.tab_body' },
  { key: 'connections', icon: '🔗', label: 'preferences.tab_connections' },
];

// какой секции на какой вкладке жить: старые ссылки /preferences#vdot-section продолжают работать
const SECTION_TAB = {
  'week-section': 'training',
  'goals-section': 'training',
  'location-section': 'training',
  'vdot-section': 'body',
  'weekly-volume-section': 'body',
  'hr-max-section': 'body',
  'weight-section': 'body',
  'strava-section': 'connections',
  'telegram-section': 'connections',
  'notifications-section': 'connections',
};

const TAB_STORAGE_KEY = 'preferences.tab';

export const PreferencesPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { athlete, checkAuth } = useAuth();

  const [highlightedId, setHighlightedId] = useState(null);
  const handledHash = useRef(null);
  const highlightTimer = useRef(null);

  const [tab, setTab] = useState(() => {
    const fromHash = SECTION_TAB[location.hash.slice(1)];
    if (fromHash) return fromHash;
    try {
      const saved = localStorage.getItem(TAB_STORAGE_KEY);
      if (TABS.some((x) => x.key === saved)) return saved;
    } catch { /* ignore */ }
    return 'training';
  });

  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch { /* ignore */ }
  }, [tab]);

  // Переход по #hash: переключаем вкладку, потом скроллим к секции (один раз на переход)
  useEffect(() => {
    if (!location.hash) return;
    const hashKey = `${location.key}${location.hash}`;
    if (handledHash.current === hashKey) return;

    const id = location.hash.slice(1);
    const targetTab = SECTION_TAB[id];
    if (targetTab && targetTab !== tab) {
      setTab(targetTab);                // эффект перезапустится уже с нужной вкладкой
      return;
    }

    const el = document.getElementById(id);
    if (!el) return;
    handledHash.current = hashKey;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightedId(id);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightedId(null), 2000);
  }, [location.hash, location.key, tab]);

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  const panelProps = { athlete, checkAuth, highlightedId };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">{t('preferences.title')}</h1>
        <Link to="/dashboard" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
          ← {t('preferences.back_to_dashboard')}
        </Link>
      </div>

      <div role="tablist" className="flex gap-1 overflow-x-auto bg-white rounded-xl shadow p-1">
        {TABS.map((x) => {
          const active = tab === x.key;
          return (
            <button
              key={x.key}
              type="button"
              role="tab"
              id={`tab-${x.key}`}
              aria-selected={active}
              aria-controls={`panel-${x.key}`}
              onClick={() => setTab(x.key)}
              className={`flex-1 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {x.icon} {t(x.label)}
            </button>
          );
        })}
      </div>

      {/* Все вкладки смонтированы и только прячутся: введённые, но несохранённые значения не теряются при переключении */}
      <div role="tabpanel" id="panel-training" aria-labelledby="tab-training" hidden={tab !== 'training'}>
        <TrainingTab athlete={athlete} highlightedId={highlightedId} />
      </div>
      <div role="tabpanel" id="panel-body" aria-labelledby="tab-body" hidden={tab !== 'body'}>
        <BodyTab {...panelProps} />
      </div>
      <div role="tabpanel" id="panel-connections" aria-labelledby="tab-connections" hidden={tab !== 'connections'}>
        <ConnectionsTab {...panelProps} />
      </div>
    </div>
  );
};